import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion'
import {
  Menu,
  X,
  PlayCircle,
  Sparkles,
  Calendar,
  MessageCircle,
  ShieldCheck,
  UserCheck,
  Lock,
  Check,
  ChevronDown,
  HelpCircle,
  Heart,
  Stethoscope,
  Brain,
  Activity,
  Ear,
  Users,
  FileText,
  BellRing,
  Mic,
  Search,
  Clock,
  Quote,
  ArrowRight,
  Linkedin,
  Instagram,
  Youtube,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import SchedulingChat from './SchedulingChat'
import { Logo } from './Logo'
import { track, FORM_ANALYTICS, type CtaLocation } from '@/lib/analytics'
import {
  MockWeeklySummary,
  MockWhatsToStructured,
  MockAlerts,
  MockEvolution,
} from './ProductMockups'

// ============================================
// ANIMATION VARIANTS
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
}

const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
}

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
}

// ============================================
// COMPONENTS
// ============================================

interface BadgeProps {
  children: React.ReactNode
  icon?: React.ReactNode
  className?: string
}

const Badge = ({ children, icon, className }: BadgeProps) => (
  <div className={cn(
    "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-200 shadow-sm text-brand-700 text-sm font-medium",
    className
  )}>
    {icon}
    {children}
  </div>
)

interface FeatureCardProps {
  children: React.ReactNode
  className?: string
}

const FeatureCard = ({ children, className }: FeatureCardProps) => (
  <motion.div
    variants={fadeInUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-50px" }}
    className={cn(
      "card-elevated rounded-3xl p-6 relative overflow-hidden transition-all duration-400",
      "hover:translate-y-[-6px] hover:border-brand-200/50 hover:shadow-xl",
      className
    )}
  >
    {children}
  </motion.div>
)

interface FAQItemProps {
  question: string
  answer: string
  isOpen: boolean
  onClick: () => void
}

const FAQItem = ({ question, answer, isOpen, onClick }: FAQItemProps) => (
  <div className="card-elevated rounded-2xl overflow-hidden">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-6 text-left"
    >
      <span className="font-medium text-surface-900 pr-4">{question}</span>
      <ChevronDown className={cn(
        "w-5 h-5 text-surface-400 shrink-0 transition-transform duration-300",
        isOpen && "rotate-180"
      )} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="px-6 pb-6">
            <p className="text-surface-600 leading-relaxed">{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

// Section header (badge + title + subtitle)
interface SectionHeaderProps {
  badge: React.ReactNode
  badgeIcon?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  center?: boolean
  className?: string
}

const SectionHeader = ({ badge, badgeIcon, title, subtitle, center = true, className }: SectionHeaderProps) => (
  <motion.div
    className={cn(center ? "text-center mb-16 lg:mb-20 max-w-2xl mx-auto" : "mb-16 lg:mb-20 max-w-2xl", className)}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={stagger}
  >
    <motion.div variants={fadeInUp} className={center ? "flex justify-center" : ""}>
      <Badge icon={badgeIcon}>{badge}</Badge>
    </motion.div>
    <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-surface-900 tracking-tight mb-3 sm:mb-4 mt-4 sm:mt-6">
      {title}
    </motion.h2>
    {subtitle && (
      <motion.p variants={fadeInUp} className="text-surface-600 text-base sm:text-lg lg:text-xl">
        {subtitle}
      </motion.p>
    )}
  </motion.div>
)

// ============================================
// MAIN COMPONENT
// ============================================

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [chatSource, setChatSource] = useState<CtaLocation>('hero')

  const openChat = (location: CtaLocation) => {
    track('select_content', {
      content_type: 'cta',
      content_id: 'agendar_demonstracao',
      cta_location: location,
    })
    track('form_start', { ...FORM_ANALYTICS, source: location })
    setChatSource(location)
    setChatOpen(true)
  }
  const scrollRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll()
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  })

  // Close mobile menu on navigation
  const handleNavClick = () => {
    setMobileMenuOpen(false)
  }

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileMenuOpen])

  const navLinks = [
    { href: '#como-funciona', label: 'Como Funciona' },
    { href: '#o-sistema', label: 'O Sistema' },
    { href: '#para-quem', label: 'Para quem é' },
    { href: '#demonstracao', label: 'Demonstração' },
  ]

  const faqItems = [
    {
      question: 'Como a família envia os relatos?',
      answer: 'A família manda mensagens normalmente para o número de WhatsApp da clínica — texto, áudio ou foto, como já faz. Não precisa baixar app nem fazer cadastro. Funciona como qualquer conversa de WhatsApp.'
    },
    {
      question: 'A IA dá diagnóstico ou decide algo?',
      answer: 'Não. A IA apenas organiza o que a família relatou — humor, sono, alimentação, medicação, comportamento — e destaca o que mudou. Nenhuma conclusão clínica é automática: toda decisão é sua. Pense nela como uma assistente que prepara o material para a sua leitura.'
    },
    {
      question: 'Os dados dos pacientes estão seguros?',
      answer: 'Sim. Trabalhamos com comunicação criptografada, conformidade com a LGPD e isolamento total de dados por clínica. Você pode solicitar a exclusão dos dados a qualquer momento.'
    },
    {
      question: 'Preciso mudar a forma como a família já se comunica?',
      answer: 'Não. A ideia é justamente aproveitar o que já acontece: as famílias relatam pelo WhatsApp como sempre fizeram. O SecBrain organiza esse relato para você, sem criar mais um canal para ninguém gerenciar.'
    },
    {
      question: 'Quanto tempo leva para começar?',
      answer: 'O setup é rápido: configuramos o WhatsApp da clínica e cadastramos os pacientes junto com você. A partir daí, cada relato que chega já entra organizado no painel.'
    },
  ]

  return (
    <div ref={scrollRef} className="min-h-screen w-full bg-white font-sans">

      {/* Scroll Progress Bar */}
      <motion.div
        style={{ scaleX: smoothProgress }}
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-500 to-accent-light origin-left z-[100]"
      />

      {/* ==================== NAVIGATION ==================== */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="transition-transform duration-300 group-hover:scale-105">
                <Logo size={40} />
              </div>
              <div className="absolute inset-0 bg-brand-400 rounded-xl animate-ping opacity-0 group-hover:opacity-75" style={{ animationDuration: '1s' }} />
            </div>
            <div className="flex flex-col">
              <span className="text-surface-900 font-semibold text-lg tracking-tight leading-tight">SecBrain</span>
              <span className="text-brand-600 text-[10px] font-medium tracking-wide uppercase">Neurodivergente</span>
            </div>
          </a>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-surface-600 hover:text-surface-900 text-sm font-medium relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-0.5 after:bg-gradient-to-r after:from-brand-500 after:to-accent-light hover:after:w-full after:transition-all after:duration-300"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a href="https://app.neurodivergente.secbra.in/" className="text-surface-600 hover:text-surface-900 text-sm font-medium transition-colors">
              Entrar
            </a>
            <a
              href="https://app.neurodivergente.secbra.in/signup?source=landing"
              className="text-surface-600 hover:text-surface-900 text-sm font-medium transition-colors"
            >
              Criar conta grátis
            </a>
            <button
              onClick={() => openChat('nav_desktop')}
              className="btn-primary text-white px-6 py-2.5 rounded-full text-sm font-semibold"
            >
              Agendar Demo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 text-surface-600 hover:text-surface-900 transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] bg-white lg:hidden"
          >
            <div className="p-6 h-full flex flex-col">
              <div className="flex justify-between items-center mb-12">
                <a href="#" className="flex items-center gap-3">
                  <Logo size={40} />
                  <div className="flex flex-col">
                    <span className="text-surface-900 font-semibold text-lg">SecBrain</span>
                    <span className="text-brand-600 text-[10px] font-medium tracking-wide uppercase">Neurodivergente</span>
                  </div>
                </a>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-surface-500 hover:text-surface-900">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={handleNavClick}
                    className="text-surface-900 text-2xl font-medium py-4 border-b border-surface-100 hover:text-brand-600 transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
              <div className="pt-6 space-y-3">
                <a href="https://app.neurodivergente.secbra.in/" className="block text-center text-surface-600 font-medium py-3 border border-surface-200 rounded-full hover:bg-surface-50 transition-colors">
                  Entrar
                </a>
                <button
                  onClick={() => {
                    handleNavClick()
                    openChat('nav_mobile')
                  }}
                  className="btn-primary block w-full text-center text-white font-semibold py-3 rounded-full"
                >
                  Agendar Demonstração
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== HERO SECTION ==================== */}
      <section className="hero-gradient grid-pattern relative pt-32 pb-24 lg:pb-32 px-6 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-brand-200 rounded-full blur-[120px] opacity-40" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-100 rounded-full blur-[150px] opacity-50" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Left: Content */}
            <motion.div
              className="lg:col-span-6 xl:col-span-6"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {/* Badge */}
              <motion.div variants={fadeInUp}>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-200 shadow-sm mb-8">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-500" />
                  </span>
                  <span className="text-brand-700 text-sm font-medium">WhatsApp + IA · para clínicas de TEA</span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={fadeInUp}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-semibold text-surface-900 leading-[1.12] tracking-tight mb-4 sm:mb-6"
              >
                Chegue em cada sessão sabendo{' '}
                <span className="text-gradient font-serif italic">como foi a semana</span>{' '}
                do paciente.
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                variants={fadeInUp}
                className="text-base sm:text-lg lg:text-xl text-surface-600 leading-relaxed mb-8 sm:mb-10 max-w-lg"
              >
                As famílias relatam pelo WhatsApp, como já fazem. A IA organiza em histórico e padrões — você revisa, decide e conduz a devolutiva com tudo na mão.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={() => openChat('hero')}
                  className="btn-primary inline-flex items-center gap-2 text-white px-8 py-4 rounded-full font-semibold text-base"
                >
                  <Calendar className="w-4 h-4" />
                  Agendar Demonstração
                </button>
                <a href="#como-funciona" className="btn-secondary inline-flex items-center gap-2 text-surface-700 px-8 py-4 rounded-full font-medium text-base">
                  <PlayCircle className="w-5 h-5 text-brand-600" />
                  Como funciona
                </a>
              </motion.div>

              {/* Trust Signals */}
              <motion.div variants={fadeIn} className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-surface-600">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-600" />
                  <span>Já em uso por clínicas-piloto</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-600" />
                  <span>Sem app para a família baixar</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Visual — mockup fiel do resultado */}
            <div className="lg:col-span-6 xl:col-span-6 relative">
              <motion.div variants={scaleIn} initial="hidden" animate="visible" className="relative">
                <div className="absolute -inset-4 bg-brand-200/30 rounded-[2rem] blur-2xl -z-10" />
                <MockWeeklySummary />

                {/* Floating alert card */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="hidden sm:block absolute -right-3 sm:-right-5 -bottom-5 card-elevated rounded-xl px-4 py-3 shadow-lg bg-white"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <BellRing className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-surface-900">Alerta</div>
                      <div className="text-xs text-surface-500">Padrão identificado</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TRUST STRIP ==================== */}
      <section className="py-7 px-6 bg-white border-y border-surface-200">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 text-sm text-surface-600">
          <div className="flex items-center gap-2.5">
            <UserCheck className="w-5 h-5 text-brand-600 shrink-0" />
            <span><strong className="font-semibold text-surface-800">A IA organiza</strong>, você decide</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-surface-200" />
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5 text-brand-600 shrink-0" />
            <span>Conformidade com a <strong className="font-semibold text-surface-800">LGPD</strong></span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-surface-200" />
          <div className="flex items-center gap-2.5">
            <MessageCircle className="w-5 h-5 text-brand-600 shrink-0" />
            <span>Texto, áudio ou foto — <strong className="font-semibold text-surface-800">sem app</strong></span>
          </div>
        </div>
      </section>

      {/* ==================== A DOR (ANTES) ==================== */}
      <section id="a-dor" className="py-24 lg:py-32 px-6 bg-surface-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            badge="O problema"
            badgeIcon={<Search className="w-4 h-4" />}
            title={<>Reconhece <span className="text-gradient font-serif italic">esta cena?</span></>}
            subtitle="O relato da família existe. Ele só não chega organizado na hora em que você precisa."
          />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: Mic, title: 'Áudios o dia todo', desc: 'Texto, áudio de 4 minutos e foto chegam a qualquer hora. Antes da sessão, você ouve tudo de novo para lembrar o que aconteceu.' },
              { icon: MessageCircle, title: '“Como foi a semana?”', desc: 'Na consulta, você depende da memória da família — e os detalhes do dia a dia se perdem pelo caminho.' },
              { icon: Clock, title: 'Devolutiva na correria', desc: 'O resumo é montado de cabeça, sem ver o padrão das últimas semanas. O que é impressão e o que é tendência?' },
            ].map((item, i) => (
              <FeatureCard key={i}>
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-surface-900 mb-2">{item.title}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{item.desc}</p>
              </FeatureCard>
            ))}
          </div>

          <motion.p
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            className="text-center text-surface-700 text-lg lg:text-xl font-medium mt-12 max-w-3xl mx-auto"
          >
            O WhatsApp virou o canal — mas, sem organização, ele também vira <span className="text-brand-700">gargalo</span>.
          </motion.p>
        </div>
      </section>

      {/* ==================== A VIRADA (COMO FUNCIONA) ==================== */}
      <section id="como-funciona" className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="Como funciona"
            badgeIcon={<Sparkles className="w-4 h-4" />}
            title={<>O relato que já chega, <span className="text-gradient font-serif italic">organizado</span> para a sua decisão</>}
            subtitle="Sem mudar nada para a família. O SecBrain transforma a conversa do WhatsApp em contexto clínico."
          />

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={scaleIn}>
            <MockWhatsToStructured />
          </motion.div>

          {/* 3 passos */}
          <div className="grid sm:grid-cols-3 gap-6 mt-14">
            {[
              { n: '1', icon: MessageCircle, title: 'A família relata', desc: 'Texto, áudio ou foto pelo WhatsApp, como já faz.' },
              { n: '2', icon: Sparkles, title: 'A IA organiza', desc: 'Humor, sono, alimentação, medicação e comportamento, estruturados.' },
              { n: '3', icon: UserCheck, title: 'Você decide', desc: 'Revisa o resumo, vê o padrão e conduz a devolutiva.' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
                className="relative flex flex-col items-center text-center px-2"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 text-white flex items-center justify-center mb-4 shadow-md">
                  <step.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-surface-900 mb-1">
                  <span className="text-brand-500 mr-1">{step.n}.</span>{step.title}
                </h3>
                <p className="text-surface-600 text-sm leading-relaxed">{step.desc}</p>
                {i < 2 && (
                  <ArrowRight className="hidden sm:block absolute -right-3 top-4 w-5 h-5 text-brand-300" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== O SISTEMA (PRODUTO) ==================== */}
      <section id="o-sistema" className="py-24 lg:py-32 px-6 bg-surface-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="O sistema"
            badgeIcon={<FileText className="w-4 h-4" />}
            title={<>O que você <span className="text-gradient font-serif italic">vê</span> no SecBrain</>}
            subtitle="Não é promessa — é o que o profissional usa no dia a dia."
          />

          <div className="space-y-16 lg:space-y-24">
            {/* Bloco A — Análise Semanal */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={scaleIn}>
                <MockWeeklySummary />
              </motion.div>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                <motion.h3 variants={fadeInUp} className="text-2xl lg:text-3xl font-semibold text-surface-900 mb-4">
                  A análise da semana, <span className="text-brand-600">pronta</span>
                </motion.h3>
                <motion.p variants={fadeInUp} className="text-surface-600 leading-relaxed mb-6">
                  Cada relato que chega entra organizado por humor, sono, alimentação, medicação e comportamento — com os pontos de atenção em destaque. Você abre o paciente e já sabe como foi a semana.
                </motion.p>
                <motion.ul variants={fadeInUp} className="space-y-3">
                  {['Resumo por indicador, baseado nos relatos reais', 'Pontos de atenção destacados automaticamente', 'Pronto para a devolutiva, sem digitação manual'].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-surface-700">
                      <span className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-brand-600" />
                      </span>
                      <span className="text-sm">{b}</span>
                    </li>
                  ))}
                </motion.ul>
              </motion.div>
            </div>

            {/* Bloco B — Alertas (imagem à direita) */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="lg:order-2">
                <motion.h3 variants={fadeInUp} className="text-2xl lg:text-3xl font-semibold text-surface-900 mb-4">
                  Alertas quando algo <span className="text-brand-600">muda</span>
                </motion.h3>
                <motion.p variants={fadeInUp} className="text-surface-600 leading-relaxed mb-6">
                  Sono irregular há dias, recusa alimentar aumentando, crises mais frequentes. O sistema sinaliza por paciente e por severidade — para você agir antes da próxima consulta, não depois.
                </motion.p>
                <motion.ul variants={fadeInUp} className="space-y-3">
                  {['Severidade clara: média, alta e crítica', 'Organizados por paciente', 'Baseados na mudança de padrão, não em alarme falso'].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-surface-700">
                      <span className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-brand-600" />
                      </span>
                      <span className="text-sm">{b}</span>
                    </li>
                  ))}
                </motion.ul>
              </motion.div>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={scaleIn} className="lg:order-1">
                <MockAlerts />
              </motion.div>
            </div>

            {/* Bloco C — Evolução */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={scaleIn}>
                <MockEvolution />
              </motion.div>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                <motion.h3 variants={fadeInUp} className="text-2xl lg:text-3xl font-semibold text-surface-900 mb-4">
                  Evolução, não <span className="text-brand-600">impressão</span>
                </motion.h3>
                <motion.p variants={fadeInUp} className="text-surface-600 leading-relaxed mb-6">
                  O histórico separa o que é tendência do que é impressão da última consulta. Você acompanha a evolução real do paciente ao longo das semanas — com evidência para mostrar à família.
                </motion.p>
                <motion.ul variants={fadeInUp} className="space-y-3">
                  {['Linha do tempo por indicador', 'Compare semanas e veja a tendência', 'Evidência objetiva para a devolutiva'].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-surface-700">
                      <span className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-brand-600" />
                      </span>
                      <span className="text-sm">{b}</span>
                    </li>
                  ))}
                </motion.ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== IA ORGANIZA, VOCÊ DECIDE ==================== */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="card-elevated rounded-[2.5rem] p-8 sm:p-12 lg:p-16 relative overflow-hidden bg-gradient-to-br from-brand-50/60 to-white"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-brand-100/50 rounded-full blur-3xl -z-0" />
            <div className="relative z-10">
              <div className="max-w-2xl mb-10">
                <motion.div variants={fadeInUp}>
                  <Badge icon={<UserCheck className="w-4 h-4" />} className="bg-white border-brand-200">Confiança</Badge>
                </motion.div>
                <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-surface-900 tracking-tight mb-3 mt-5">
                  A IA organiza. <span className="text-gradient font-serif italic">Quem decide é você.</span>
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-surface-600 text-base sm:text-lg">
                  Em saúde, a tecnologia tem que ser apoio — nunca substituto. O SecBrain foi desenhado com essa fronteira clara.
                </motion.p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { icon: UserCheck, title: 'Sem diagnóstico automático', desc: 'A IA estrutura o relato e aponta o que mudou. A leitura clínica é sempre sua.' },
                  { icon: ShieldCheck, title: 'Conformidade com a LGPD', desc: 'Tratamento de dados transparente, com base legal e direito à exclusão.' },
                  { icon: Lock, title: 'Dados isolados por clínica', desc: 'Cada organização enxerga apenas os próprios pacientes. Comunicação criptografada.' },
                  { icon: Heart, title: 'Família sem fricção', desc: 'Ninguém baixa app nem aprende ferramenta nova. Usam o WhatsApp que já têm.' },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp} className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-surface-100">
                    <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-brand-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-surface-900 mb-1">{item.title}</h3>
                      <p className="text-surface-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== QUEM JÁ USA (PROVA) ==================== */}
      {/* TODO: substituir pelos depoimentos reais dos pilotos, com autorização. */}
      <section className="py-24 lg:py-32 px-6 bg-surface-50">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            badge="Quem já usa"
            badgeIcon={<Heart className="w-4 h-4" />}
            title={<>A palavra de quem está <span className="text-gradient font-serif italic">testando</span></>}
            subtitle="Profissionais em fase piloto já usam o SecBrain para aproximar a rotina da família da leitura clínica."
          />

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                quote:
                  'O WhatsApp reduziu a distância entre a rotina da família e a leitura clínica. Os relatos chegam mais organizados e fica mais fácil perceber padrões antes da sessão.',
                papel: 'Coordenação clínica · TEA',
                ctx: 'Clínica multidisciplinar',
              },
              {
                quote:
                  'A família não precisa aprender uma ferramenta nova. Ela manda áudio, texto ou imagem, e a equipe recebe um resumo mais claro para acompanhar evolução e sinais de atenção.',
                papel: 'Psicologia / ABA',
                ctx: 'Consultório',
              },
            ].map((d, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
                className="rounded-3xl border border-surface-100 bg-white p-8 shadow-soft flex flex-col"
              >
                <div className="mb-5">
                  <Quote className="w-8 h-8 text-brand-300" />
                </div>
                <p className="text-surface-700 italic leading-relaxed flex-1">
                  “{d.quote}”
                </p>
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-surface-100">
                  <div className="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center text-brand-500">
                    <Heart className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-surface-800 text-sm">{d.papel}</div>
                    <div className="text-xs text-surface-500">{d.ctx}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PARA QUEM É ==================== */}
      <section id="para-quem" className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            badge="Para quem é"
            badgeIcon={<Heart className="w-4 h-4" />}
            title={<>Feito para quem cuida de pacientes <span className="text-gradient font-serif italic">neurodivergentes</span></>}
            subtitle="Profissionais e equipes que acompanham o dia a dia de perto — e precisam enxergar padrões com clareza."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: Stethoscope, title: 'Neuropediatria', desc: 'Acompanhe sono, alimentação e comportamento entre as consultas, sem depender da memória da família.' },
              { icon: Brain, title: 'Psiquiatria Infantil', desc: 'Veja a resposta à medicação e as mudanças de humor ao longo das semanas, com histórico organizado.' },
              { icon: Heart, title: 'Psicologia e ABA', desc: 'Registre a evolução de comportamentos relatados em casa, no contexto real do dia a dia.' },
              { icon: Activity, title: 'Terapia Ocupacional', desc: 'Monitore rotina, autonomia e regulação sensorial com relatos frequentes e estruturados.' },
              { icon: Ear, title: 'Fonoaudiologia', desc: 'Acompanhe comunicação e alimentação com exemplos reais enviados por texto, áudio ou foto.' },
              { icon: Users, title: 'Clínica Multidisciplinar', desc: 'Centralize os relatos de cada paciente para que toda a equipe trabalhe com a mesma informação.' },
            ].map((item, i) => (
              <FeatureCard key={i}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center mb-5 shadow-md">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-surface-900 mb-2">{item.title}</h3>
                <p className="text-surface-600 text-sm leading-relaxed">{item.desc}</p>
              </FeatureCard>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DEMONSTRAÇÃO / CTA ==================== */}
      <section id="demonstracao" className="py-24 lg:py-32 px-6 bg-surface-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="card-elevated rounded-[2.5rem] p-10 lg:p-16 text-center relative overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {/* Decorative gradient */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-brand-100/50 to-transparent rounded-full blur-3xl -z-0" />

            <div className="relative z-10">
              <motion.div variants={fadeInUp} className="flex justify-center">
                <Badge icon={<Sparkles className="w-4 h-4" />} className="bg-brand-50 border-brand-200">
                  Agende uma demonstração
                </Badge>
              </motion.div>

              <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-surface-900 tracking-tight mb-3 sm:mb-4 mt-4 sm:mt-6">
                Veja o SecBrain com os <span className="text-gradient font-serif italic">seus pacientes</span>
              </motion.h2>

              <motion.p variants={fadeInUp} className="text-surface-600 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-6 sm:mb-8">
                Uma conversa rápida para entender a sua rotina e mostrar como ficaria o acompanhamento da sua clínica.
              </motion.p>

              {/* Features list */}
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-10 text-sm text-surface-600">
                {['Demonstração guiada de 30 min', 'Onboarding com a equipe', 'Sem compromisso'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center">
                      <Check className="w-3 h-3 text-brand-600" />
                    </div>
                    <span>{feature}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div variants={fadeInUp}>
                <button
                  onClick={() => openChat('demonstracao')}
                  className="btn-primary inline-flex items-center gap-3 text-white px-10 py-5 rounded-full font-semibold text-lg"
                >
                  <Calendar className="w-5 h-5" />
                  Agendar Demonstração
                </button>
              </motion.div>

              <motion.p variants={fadeIn} className="text-surface-400 text-sm mt-6">
                Resposta em até 24h • Demonstração de 30 minutos
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="flex justify-center">
              <Badge icon={<HelpCircle className="w-4 h-4" />}>Dúvidas</Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-surface-900 tracking-tight mb-3 sm:mb-4 mt-4 sm:mt-6">
              Perguntas <span className="text-gradient font-serif italic">frequentes</span>
            </motion.h2>
          </motion.div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <FAQItem
                key={i}
                question={item.question}
                answer={item.answer}
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section className="py-24 lg:py-32 px-6 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-500 rounded-full blur-[150px] opacity-30" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-400 rounded-full blur-[120px] opacity-20" />

        <motion.div
          className="max-w-4xl mx-auto text-center relative z-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-white tracking-tight mb-4 sm:mb-6">
            Chegue preparado na<br className="hidden sm:block" /> próxima sessão.
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-brand-100 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto">
            Agende uma demonstração e veja como o SecBrain transforma o relato das famílias em contexto clínico pronto para você.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <button
              onClick={() => openChat('cta_final')}
              className="inline-flex items-center gap-3 bg-white text-brand-700 px-10 py-5 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:bg-brand-50 transition-all"
            >
              <Calendar className="w-5 h-5" />
              Agendar Demonstração
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-surface-900 text-white py-16 lg:py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            {/* Brand */}
            <div className="lg:col-span-2">
              <a href="#" className="flex items-center gap-3 mb-6">
                <Logo size={40} />
                <div className="flex flex-col">
                  <span className="text-white font-semibold text-lg">SecBrain</span>
                  <span className="text-brand-400 text-[10px] font-medium tracking-wide uppercase">Neurodivergente</span>
                </div>
              </a>
              <p className="text-surface-400 mb-6 max-w-xs leading-relaxed">
                Acompanhamento inteligente para pacientes neurodivergentes. Conectando famílias e profissionais.
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/secbrain-neurodivergente/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-surface-800 flex items-center justify-center text-surface-400 hover:bg-brand-600 hover:text-white transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/secbrain.ai" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-surface-800 flex items-center justify-center text-surface-400 hover:bg-brand-600 hover:text-white transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.youtube.com/@SecBrainNeurodivergente" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-surface-800 flex items-center justify-center text-surface-400 hover:bg-brand-600 hover:text-white transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Links - Produto */}
            <div>
              <h4 className="font-semibold mb-5">Produto</h4>
              <ul className="space-y-3 text-surface-400">
                <li><a href="#como-funciona" className="hover:text-white transition-colors">Como Funciona</a></li>
                <li><a href="#o-sistema" className="hover:text-white transition-colors">O Sistema</a></li>
                <li><a href="#demonstracao" className="hover:text-white transition-colors">Demonstração</a></li>
              </ul>
            </div>

            {/* Links - Empresa */}
            <div>
              <h4 className="font-semibold mb-5">Empresa</h4>
              <ul className="space-y-3 text-surface-400">
                <li><Link to="/sobre" className="hover:text-white transition-colors">Sobre Nós</Link></li>
                <li><Link to="/contato" className="hover:text-white transition-colors">Contato</Link></li>
              </ul>
            </div>

            {/* Links - Legal */}
            <div>
              <h4 className="font-semibold mb-5">Legal</h4>
              <ul className="space-y-3 text-surface-400">
                <li><Link to="/termos-de-uso" className="hover:text-white transition-colors">Termos de Uso</Link></li>
                <li><Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link></li>
                <li><Link to="/lgpd" className="hover:text-white transition-colors">LGPD</Link></li>
                <li><Link to="/exclusao-de-dados" className="hover:text-white transition-colors">Exclusão de Dados</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-surface-800 pt-8">
            <p className="text-surface-500 text-sm text-center">© {new Date().getFullYear()} SecBrain Neurodivergente. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Scheduling Chat */}
      <SchedulingChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        source={chatSource}
      />
    </div>
  )
}

export default LandingPage
